//#region IMPORTS
import {
  isAnyOf,
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import {
  InstoreOrderType,
  InstoreOrderTypeState,
  InstoreOrderTypeParams,
} from "../../models/instoreOrderType";
import { FieldValues } from "react-hook-form";
import { RootState } from "../../store/configureStore";
import { toast } from "react-toastify";
import getAxiosParams from "../../helpers/getAxiosParams";
import agent from "../../api/agent";
//#endregion

//#region INIT
const initialExtraState: InstoreOrderTypeState = {
  instoreOrderTypes_Loaded: false,
  instoreOrderType_params: initParams(),
  metaData: undefined,
  loading: false,
  status: "iddle",
};

// Set initial query parameters
function initParams(): InstoreOrderTypeParams {
  return {
    page: 1,
    limit: 10000,
  };
}
//#endregion

//#region CREATE ADAPTER
const instoreOrderTypesAdapter = createEntityAdapter<InstoreOrderType>();
//#endregion

//#region READ
// Fetch All
export const fetchInstoreOrderTypesAsync = createAsyncThunk<
  InstoreOrderType[],
  void,
  { state: RootState }
>("instoreOrderTypes/fetchInstoreOrderTypesAsync", async (_, thunkAPI) => {
  const instoreOrderTypeParams = getAxiosParams(
    thunkAPI.getState().instoreOrderTypes.instoreOrderType_params,
  );
  try {
    const { data, meta } = await agent.InstoreOrderTypes.list(
      instoreOrderTypeParams,
    );
    thunkAPI.dispatch(setInstoreOrderTypeMetaData(meta));
    return data;
  } catch (error: any) {
    const raw = error?.response?.data?.message ?? error?.message;
    const message =
      typeof raw === "string"
        ? raw
        : error?.response?.status === 403
          ? "You don't have permission to view instore order types."
          : "Something went wrong while loading instore order types.";
    return thunkAPI.rejectWithValue(message);
  }
});

// Fetch by id
export const fetchInstoreOrderTypeAsync = createAsyncThunk<
  InstoreOrderType,
  number,
  { state: RootState }
>(
  "instoreOrderTypes/fetchInstoreOrderTypeAsync",
  async (instoreOrderType_id, thunkAPI) => {
    try {
      const { data } =
        await agent.InstoreOrderTypes.detail(instoreOrderType_id);
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ error: error.data });
    }
  },
);
//#endregion

//#region CREATE
export const createInstoreOrderTypeAsync = createAsyncThunk<
  InstoreOrderType,
  { values: FieldValues | null }
>(
  "instoreOrderTypes/createInstoreOrderTypeAsync",
  async ({ values }, thunkAPI) => {
    try {
      const { data } = await agent.InstoreOrderTypes.create({ ...values });
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ error: error.data });
    }
  },
);
//#endregion

//#region UPDATE
export const updateInstoreOrderTypeAsync = createAsyncThunk<
  InstoreOrderType,
  { id: number; values: FieldValues }
>(
  "instoreOrderTypes/updateInstoreOrderTypeAsync",
  async ({ id, values }, thunkAPI) => {
    try {
      const { data } = await agent.InstoreOrderTypes.update(id, { ...values });
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ error: error.data });
    }
  },
);
//#endregion

//#region DELETE
export const deleteInstoreOrderTypeAsync = createAsyncThunk<
  number,
  { id: number }
>("instoreOrderTypes/deleteInstoreOrderTypeAsync", async ({ id }, thunkAPI) => {
  try {
    await agent.InstoreOrderTypes.delete(id);
    return id;
  } catch (error: any) {
    return thunkAPI.rejectWithValue({ error: error.data });
  }
});
//#endregion

//#region CREATE REDUCER SLICE
export const instoreOrderTypeSlice = createSlice({
  name: "instoreOrderTypes",
  initialState:
    instoreOrderTypesAdapter.getInitialState<InstoreOrderTypeState>(
      initialExtraState,
    ),
  reducers: {
    setInstoreOrderTypeParams: (state, action) => {
      state.instoreOrderTypes_Loaded = false;
      state.instoreOrderType_params = {
        ...state.instoreOrderType_params,
        ...action.payload,
        page: 1,
      };
    },
    clearInstoreOrderTypeParams: (state) => {
      state.instoreOrderTypes_Loaded = false;
      state.instoreOrderType_params = initParams();
    },
    clearInstoreOrderTypeParam: (state, action) => {
      const newParmams = { ...state.instoreOrderType_params };
      delete newParmams[action.payload];
      state.instoreOrderTypes_Loaded = false;
      state.instoreOrderType_params = newParmams;
    },
    setInstoreOrderTypePageNumber: (state, action) => {
      state.instoreOrderTypes_Loaded = false;
      state.instoreOrderType_params = {
        ...state.instoreOrderType_params,
        ...action.payload,
      };
    },
    setInstoreOrderTypeMetaData: (state, action) => {
      state.metaData = action.payload;
    },
    resetInstoreOrderTypeParams: (state) => {
      state.instoreOrderTypes_Loaded = false;
      state.instoreOrderType_params = initParams();
    },
    clearInstoreOrderTypes: () =>
      instoreOrderTypesAdapter.getInitialState<InstoreOrderTypeState>(
        initialExtraState,
      ),
  },
  extraReducers: (builder) => {
    // FETCH instoreOrderTypes
    builder.addCase(fetchInstoreOrderTypesAsync.pending, (state) => {
      state.loading = true;
      state.status = "pendingFetchInstoreOrderTypes";
    });
    builder.addCase(fetchInstoreOrderTypesAsync.fulfilled, (state, action) => {
      instoreOrderTypesAdapter.setAll(state, action.payload);
      state.instoreOrderTypes_Loaded = true;
      state.loading = false;
      state.status = "idle";
    });

    // FETCH InstoreOrderType by id
    builder.addCase(fetchInstoreOrderTypeAsync.pending, (state) => {
      state.loading = true;
      state.status = "pendingFetchInstoreOrderType";
    });
    builder.addCase(fetchInstoreOrderTypeAsync.fulfilled, (state, action) => {
      instoreOrderTypesAdapter.upsertOne(state, action.payload);
      state.loading = false;
      state.status = "idle";
    });

    // CREATE InstoreOrderType
    builder.addCase(createInstoreOrderTypeAsync.pending, (state) => {
      state.loading = true;
      state.status = "PendingCreateInstoreOrderType";
    });
    builder.addCase(createInstoreOrderTypeAsync.fulfilled, (state, action) => {
      instoreOrderTypesAdapter.addOne(state, action.payload);
      toast.success("Instore theme added");
      state.metaData &&= {
        ...state.metaData,
        total: state.metaData.total + 1,
        to: state.metaData.to + 1,
      };
      state.loading = false;
      state.status = "idle";
    });

    // UPDATE InstoreOrderType
    builder.addCase(updateInstoreOrderTypeAsync.pending, (state, action) => {
      state.loading = true;
      state.status = "PendingUpdateInstoreOrderType" + action.meta.arg.id;
    });
    builder.addCase(updateInstoreOrderTypeAsync.fulfilled, (state, action) => {
      instoreOrderTypesAdapter.upsertOne(state, action.payload);
      toast.success(`Instore theme ${action.payload.id} updated`);
      state.loading = false;
      state.status = "idle";
    });

    // DELETE InstoreOrderType
    builder.addCase(deleteInstoreOrderTypeAsync.pending, (state) => {
      state.loading = true;
      state.status = "PendingDeleteInstoreOrderType";
    });
    builder.addCase(deleteInstoreOrderTypeAsync.fulfilled, (state, action) => {
      instoreOrderTypesAdapter.removeOne(state, action.payload);
      toast.success("Instore theme deleted");
      state.loading = false;
      state.metaData &&= {
        ...state.metaData,
        total:
          state.metaData.total > 0
            ? state.metaData.total - 1
            : state.metaData.total,
        to: state.metaData.to > 0 ? state.metaData.to - 1 : state.metaData.to,
      };
      state.status = "idle";
    });

    //#region CRUD REJECTION
    builder.addMatcher(
      isAnyOf(
        fetchInstoreOrderTypeAsync.rejected,
        fetchInstoreOrderTypesAsync.rejected,
        createInstoreOrderTypeAsync.rejected,
        updateInstoreOrderTypeAsync.rejected,
        deleteInstoreOrderTypeAsync.rejected,
      ),
      (state, { payload }) => {
        toast.error(`${payload}`);
        state.loading = false;
        state.status = "idle";
      },
    );
    //#endregion
  },
});
//#endregion

//#region NORMALISED STATE
export const instoreOrderTypesSelectors = instoreOrderTypesAdapter.getSelectors(
  (state: RootState) => state.instoreOrderTypes,
);
//#endregion

//#region EXPORT
export const {
  setInstoreOrderTypePageNumber,
  setInstoreOrderTypeMetaData,
  resetInstoreOrderTypeParams,
  clearInstoreOrderTypeParams,
  clearInstoreOrderTypeParam,
  setInstoreOrderTypeParams,
  clearInstoreOrderTypes,
} = instoreOrderTypeSlice.actions;
//#endregion
